// src/pages/ChatWithAI/components/ThreadSidebar.js
import React, { useState, useEffect, useRef } from 'react';
import './ThreadSidebar.css';

/**
 * Componente de sidebar para histórico de threads
 * @param {Object} props
 * @param {boolean} props.isOpen - Se sidebar está aberta
 * @param {Function} props.onClose - Callback para fechar sidebar
 * @param {Function} props.onSelectThread - Callback ao selecionar thread
 * @param {number} props.currentThreadId - ID da thread atual
 * @param {Object} props.threadHistory - Hook useThreadHistory
 */
const ThreadSidebar = ({ 
  isOpen, 
  onClose, 
  onSelectThread, 
  currentThreadId,
  threadHistory 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const loadMoreRef = useRef(null);

  const {
    threads,
    loading,
    error,
    hasMore,
    searchMode,
    fetchThreads,
    loadMore,
    searchThreads,
    clearSearch,
    initialFetchDone
  } = threadHistory;

  // Carregar threads ao abrir sidebar.
  // O guard é a ref initialFetchDone (não `threads.length`): em erro 401 a lista
  // fica vazia e o efeito re-disparava em ciclo, martelando a API.
  useEffect(() => {
    if (isOpen && !initialFetchDone.current) {
      fetchThreads(true);
    }

    // Ao fechar depois de um erro, libertar a flag para permitir UMA nova
    // tentativa na próxima abertura (sem reabrir a porta ao ciclo).
    if (!isOpen && error) {
      initialFetchDone.current = false;
    }
  }, [isOpen, error, fetchThreads, initialFetchDone]);

  // Focar input ao abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 300);
    }
  }, [isOpen]);

  // Intersection Observer para "carregar mais"
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading || searchMode) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          console.log('🔄 Load more triggered by intersection');
          loadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMore, loading, searchMode, loadMore]);

  /**
   * Handler de pesquisa
   */
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchThreads(value);
  };

  /**
   * Limpar pesquisa
   */
  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  /**
   * Formatar data/hora
   */
  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  /**
   * Handler de seleção de thread
   */
  const handleThreadClick = (thread) => {
    console.log('🎯 Thread selected:', thread.id);
    onSelectThread(thread);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="thread-sidebar-backdrop" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`thread-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="thread-sidebar-header">
          <h2 className="thread-sidebar-title">Histórico</h2>
          <button 
            className="thread-sidebar-close-btn"
            onClick={onClose}
            title="Fechar"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M15 5L5 15M5 5L15 15" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="thread-sidebar-search">
          <div className="search-input-wrapper">
            <svg 
              className="search-icon" 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none"
            >
              <circle 
                cx="7" 
                cy="7" 
                r="5" 
                stroke="currentColor" 
                strokeWidth="1.5"
              />
              <path 
                d="M11 11L14 14" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Pesquisar..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={handleClearSearch}
                title="Limpar pesquisa"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path 
                    d="M10 4L4 10M4 4L10 10" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
          {searchMode && (
            <div className="search-mode-indicator">
              Resultados da pesquisa
            </div>
          )}
        </div>

        {/* Thread List */}
        <div className="thread-sidebar-content">
          {loading && threads.length === 0 ? (
            // Skeleton loading
            <div className="thread-list-loading">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="thread-item-skeleton">
                  <div className="skeleton-title"></div>
                  <div className="skeleton-date"></div>
                </div>
              ))}
            </div>
          ) : error ? (
            // Error state
            <div className="thread-list-error">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                <path d="M24 16V26M24 32V32.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => fetchThreads(true)}
              >
                Tentar novamente
              </button>
            </div>
          ) : threads.length === 0 ? (
            // Empty state
            <div className="thread-list-empty">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <rect x="12" y="12" width="40" height="40" rx="4" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                <path d="M20 24H44M20 32H44M20 40H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.3"/>
              </svg>
              <h3>Nenhuma conversa</h3>
              <p>Suas conversas aparecerão aqui</p>
            </div>
          ) : (
            // Thread list
            <div className="thread-list">
              {threads.map(thread => (
                <div
                  key={thread.id}
                  className={`thread-item ${currentThreadId === thread.id ? 'active' : ''}`}
                  onClick={() => handleThreadClick(thread)}
                >
                  <div className="thread-item-content">
                    <h3 className="thread-item-title">
                      {thread.titulo}
                    </h3>
                    <div className="thread-item-meta">
                      <span className="thread-item-date">
                        {formatDateTime(thread.updated_at || thread.created_at)}
                      </span>
                      {thread.message_count > 0 && (
                        <span className="thread-item-count">
                          {thread.message_count} {thread.message_count === 1 ? 'mensagem' : 'mensagens'}
                        </span>
                      )}
                    </div>
                    {thread.caso_titulo && (
                      <div className="thread-item-badge">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 3H10V10C10 10.5523 9.55228 11 9 11H3C2.44772 11 2 10.5523 2 10V3Z" stroke="currentColor" strokeWidth="1.2"/>
                          <path d="M4 3V2C4 1.44772 4.44772 1 5 1H7C7.55228 1 8 1.44772 8 2V3" stroke="currentColor" strokeWidth="1.2"/>
                        </svg>
                        <span>{thread.caso_titulo}</span>
                      </div>
                    )}
                  </div>
                  {currentThreadId === thread.id && (
                    <div className="thread-item-indicator">
                      <div className="indicator-dot"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Load More Button/Indicator */}
          {!searchMode && hasMore && threads.length > 0 && (
            <div 
              ref={loadMoreRef}
              className="thread-load-more"
            >
              {loading ? (
                <div className="thread-loading-spinner">
                  <svg className="spinner" width="24" height="24" viewBox="0 0 24 24">
                    <circle 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      fill="none"
                      strokeDasharray="31.4 31.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>Carregando...</span>
                </div>
              ) : (
                <button 
                  className="load-more-btn"
                  onClick={loadMore}
                >
                  Carregar mais
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default ThreadSidebar;